import { useQuery } from 'react-query';
import { CompoundSummaryDto } from '../../../map/dtos';
import { CompoundsApis } from '../../../../new-lib/apis/compounds.apis';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { commifyNumber } from '../../../../helpers/common';
import { styled } from 'styled-components';
import { useCallback, useContext } from 'react';
import {
  SearchContext,
  SearchContextInterface
} from '../../../search-new/context/SearchContext';
import { FiltersUtils } from '../../../../helpers/search-filters';

type Props = {
  entity: { id: number };
};

const CompoundPricesSummary = ({ entity }: Props): JSX.Element => {
  const { locale, isReady } = useRouter();
  const { t } = useTranslation(['entity', 'common']);

  const { searchFiltersState } = useContext(
    SearchContext
  ) as SearchContextInterface;

  const mapFiltersToNewKeys = useCallback(() => {
    return FiltersUtils.mapFiltersToNew(searchFiltersState.searchFilters);
  }, [searchFiltersState.searchFilters]);

  const { data: compound, isLoading } = useQuery<CompoundSummaryDto>({
    queryKey: [`compound-summary`, entity.id, searchFiltersState.searchFilters],
    queryFn: ({ signal }) =>
      CompoundsApis.getCompoundSummary(
        entity.id,
        mapFiltersToNewKeys(),
        locale,
        { signal }
      ),
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled: isReady
  });

  if (isLoading || !compound) {
    return (
      <PriceSection>
        <div className="price-container">
          <p className="price-type">
            {t('developer_start_price', { ns: 'common' })}{' '}
          </p>
          <div className="price-details">
            <ShimmerEffect className="shimmer-effect" />
          </div>
        </div>

        <div className="separator"></div>

        <div className="price-container">
          <p className="price-type">
            {t('resale_start_price', { ns: 'common' })}{' '}
          </p>
          <div className="price-details">
            <ShimmerEffect className="shimmer-effect" />
          </div>
        </div>
      </PriceSection>
    );
  }

  if (compound.isLaunch) {
    return <h3 className="headline-3 price">{t('price_tba')}</h3>;
  }

  return (
    <PriceSection>
      <div className="price-container">
        <p className="price-type">
          {t('developer_start_price', { ns: 'common' })}{' '}
        </p>
        <div className="price-details">
          {compound.prices?.developerStartingPrice ? (
            <>
              <p className="headline-1 price">
                {commifyNumber(compound.prices?.developerStartingPrice)}{' '}
              </p>
              <p className="currency">
                {compound.prices?.currency || t('egp')}
              </p>
            </>
          ) : (
            <p>-</p>
          )}
        </div>
      </div>

      <div className="separator"></div>

      <div className="price-container">
        <p className="price-type">
          {t('resale_start_price', { ns: 'common' })}{' '}
        </p>
        <div className="price-details">
          {compound.prices?.resaleStartingPrice ? (
            <>
              <p className="headline-1 price">
                {commifyNumber(compound.prices?.resaleStartingPrice)}{' '}
              </p>
              <p className="currency">
                {' '}
                {compound.prices?.currency || t('egp')}{' '}
              </p>
            </>
          ) : (
            <p>-</p>
          )}
        </div>
      </div>
    </PriceSection>
  );
};

export default CompoundPricesSummary;

const ShimmerEffect = styled.div`
  width: 130px;
  height: 31.5px;
  background: linear-gradient(to right, #f0f0f0 0%, #e0e0e0 50%, #f0f0f0 100%);
  background-size: 200% auto;
  animation: shimmer 1.2s infinite;
  border-radius: 8px;

  @keyframes shimmer {
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }
`;

const PriceSection = styled.div`
  display: flex;
  gap: 8px;

  p.price {
    color: ${props => props.theme.light.colors.black};
    font-size: 21px;
    font-family: materialBold;
    line-height: 150%;
  }
  p.currency {
    color: #697586;
    font-family: materialMedium;
    font-size: 11px;
  }

  .price-container {
    width: 294px;
    display: flex;
    flex-direction: column;
    height: 100%;

    p.price-type {
      font-size: 17px;
      font-family: materialMedium;
      color: #697586;
      line-height: 150%;
    }

    .price-details {
      display: flex;
      align-items: center;
      gap: 8px;
      p.price {
        color: ${props => props.theme.light.colors.black};
        font-size: 21px;
        font-family: materialBold;
        line-height: 150%;
      }
      p.currency {
        color: #697586;
        font-family: materialMedium;
        font-size: 11px;
      }
    }
  }
  .separator {
    height: auto;
    width: 1px;
    background-color: #d1d4d9;
  }
`;
